import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CartItemsList } from "../../stock/cart/CartItemsList";
import { PaymentMethodSelector } from "../../stock/cart/PaymentMethodSelector";
import { CartValidation } from "../../stock/cart/CartValidation";
import { SaleFormContent } from "./SaleFormContent";
import { SaleFormData } from "@/types/sale";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Textarea } from "@/components/ui/textarea";

interface SaleDialogContentProps {
  isGroupSale: boolean;
  selectedItem: any;
  saleQuantity: string;
  onQuantityChange: (value: string) => void;
  selectedProducts: any[];
  onProductsChange: (products: any[]) => void;
  isCredit: boolean;
  setIsCredit: (value: boolean) => void;
  formData: SaleFormData;
  onFormChange: (field: keyof SaleFormData, value: string) => void;
  total: number;
  selectedCustomerId: string;
  setSelectedCustomerId: (value: string) => void;
}

export const SaleDialogContent = ({
  isGroupSale,
  selectedItem,
  saleQuantity,
  onQuantityChange,
  selectedProducts,
  onProductsChange,
  isCredit,
  setIsCredit,
  formData,
  onFormChange,
  total,
  selectedCustomerId,
  setSelectedCustomerId,
}: SaleDialogContentProps) => {
  const { data: specialCustomers } = useQuery({
    queryKey: ["special-customers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("special_customers")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="space-y-6">
      {!isGroupSale && selectedItem && (
        <div>
          <Label>Produit sélectionné</Label>
          <p className="text-sm">{selectedItem.data.name}</p>
        </div>
      )}

      <div className="space-y-4">
        <Label>Client spécial (optionnel)</Label>
        <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner un client spécial" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">Aucun client spécial</SelectItem>
            {specialCustomers?.map((customer) => (
              <SelectItem key={customer.id} value={customer.id}>
                {customer.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isGroupSale ? (
        <CartItemsList 
          cartItems={selectedProducts} 
          onRemoveItem={(id) => {
            onProductsChange(selectedProducts.filter(p => p.id !== id));
          }} 
        />
      ) : (
        <div className="space-y-2">
          <Label htmlFor="quantity">Quantité</Label>
          <input
            type="number"
            id="quantity"
            value={saleQuantity}
            onChange={(e) => onQuantityChange(e.target.value)}
            min="1"
            max={selectedItem?.quantity}
            className="w-full p-2 border rounded"
          />
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Switch
          checked={isCredit}
          onCheckedChange={setIsCredit}
        />
        <Label>Vente à crédit</Label>
      </div>

      {!isCredit && (
        <PaymentMethodSelector
          selectedMethod={formData.paymentMethod}
          onMethodChange={(value) => onFormChange("paymentMethod", value)}
        />
      )}

      <div className="space-y-2">
        <Label htmlFor="seller-signature">Signature du vendeur (obligatoire)</Label>
        <Textarea
          id="seller-signature"
          value={formData.sellerSignature}
          onChange={(e) => onFormChange("sellerSignature", e.target.value)}
          className="h-20"
        />
      </div>

      {isCredit && (
        <SaleFormContent
          formData={formData}
          onFormChange={onFormChange}
          isCredit={isCredit}
          total={total}
        />
      )}

      <CartValidation />
    </div>
  );
};